import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _assertThisInitialized from '@babel/runtime/helpers/assertThisInitialized';
import _inheritsLoose from '@babel/runtime/helpers/inheritsLoose';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { createContext, createElement, Children, PureComponent, useContext } from 'react';
import PropTypes from 'prop-types';
import isNode from 'detect-node';
import { createReactKeycloakContext, createReactKeycloakProvider } from '@react-keycloak/core';
import hoistStatics from 'hoist-non-react-statics';
import Cookie from 'js-cookie';
import base64 from 'base-64';

var isServer = function isServer() {
  return isNode && typeof window === 'undefined';
};

var noops = {
  string: function string() {
    return '';
  },
  boolean: function boolean() {
    return true;
  }
}; // Keycloak singleton

var keycloakInstance; // KeycloakStub singleton

var keycloakStubInstance; // this is a fake Keycloak instance we use to initialize Keycloak on the server.
// This gets over-written as soon as Keycloak is initialized on the client.

var getKeycloakStub = function getKeycloakStub(persistor) {
  var kcTokens = persistor.getTokens();
  keycloakStubInstance = {
    login: function login() {
      return Promise.resolve();
    },
    logout: function logout() {
      return Promise.resolve();
    },
    register: function register() {
      return Promise.resolve();
    },
    accountManagement: function accountManagement() {
      return Promise.resolve();
    },
    init: function init() {
      return Promise.resolve();
    },
    createLoginUrl: noops.string,
    createLogoutUrl: noops.string,
    createAccountUrl: noops.string,
    createRegisterUrl: noops.string,
    isTokenExpired: noops.boolean,
    updateToken: function updateToken() {
      return Promise.resolve();
    },
    clearToken: noops.string,
    hasRealmRole: noops.boolean,
    hasResourceRole: noops.boolean,
    loadUserProfile: function loadUserProfile() {
      return Promise.resolve();
    },
    loadUserInfo: function loadUserInfo() {
      return Promise.resolve();
    },
    authenticated: !!kcTokens.token,
    idToken: kcTokens.idToken,
    token: kcTokens.token,
    refreshToken: kcTokens.refreshToken
  };
  return keycloakStubInstance;
};
var Keycloak = !isServer() ? require('keycloak-js') : null;
var getKeycloakInstance = function getKeycloakInstance(keycloakConfig, recreate) {
  if (recreate === void 0) {
    recreate = false;
  }

  var isServerCheck = isServer();

  if (recreate || !keycloakInstance && !isServerCheck) {
    keycloakInstance = new Keycloak(keycloakConfig);
  }

  return !isServerCheck ? keycloakInstance : keycloakStubInstance;
};

var KeycloakContext = createReactKeycloakContext({
  initialized: isServer()
}); // Context to hold KeycloakStub instance

var KeycloakStubContext = createContext({
  keycloak: null
});

var KeycloakProvider = createReactKeycloakProvider(KeycloakContext);

var KeycloakStubProvider = /*#__PURE__*/function (_React$PureComponent) {
  _inheritsLoose(KeycloakStubProvider, _React$PureComponent);

  function KeycloakStubProvider() {
    return _React$PureComponent.apply(this, arguments) || this;
  }

  var _proto = KeycloakStubProvider.prototype;

  _proto.render = function render() {
    var _this$props = this.props,
        children = _this$props.children,
        keycloakStub = _this$props.keycloakStub;
    return /*#__PURE__*/createElement(KeycloakStubContext.Provider, {
      value: keycloakStub
    }, Children.only(children));
  };

  return KeycloakStubProvider;
}(PureComponent);

process.env.NODE_ENV !== "production" ? KeycloakStubProvider.propTypes = {
  children: PropTypes.element.isRequired,
  keycloakStub: PropTypes.shape({}).isRequired
} : void 0;

var SSRKeycloakProvider = /*#__PURE__*/function (_React$PureComponent) {
  _inheritsLoose(SSRKeycloakProvider, _React$PureComponent);

  function SSRKeycloakProvider(props) {
    var _this;

    _this = _React$PureComponent.call(this, props) || this;

    _defineProperty(_assertThisInitialized(_this), "onEvent", function (event, error) {
      var _this$props2, _this$props2$onEvent;

      if (event === 'onReady') {
        if (!_this.state.keycloak.authenticated) {
          var _this$props, _this$props$persistor;

          (_this$props = _this.props) === null || _this$props === void 0 ? void 0 : (_this$props$persistor = _this$props.persistor) === null || _this$props$persistor === void 0 ? void 0 : _this$props$persistor.resetTokens();
        }
      } // Propagate events up


      (_this$props2 = _this.props) === null || _this$props2 === void 0 ? void 0 : (_this$props2$onEvent = _this$props2.onEvent) === null || _this$props2$onEvent === void 0 ? void 0 : _this$props2$onEvent.call(_this$props2, event, error);
    });

    _defineProperty(_assertThisInitialized(_this), "onTokens", function (tokens) {
      var _this$props3, _this$props3$persisto, _this$props4, _this$props4$onTokens;

      // Persist updated tokens
      (_this$props3 = _this.props) === null || _this$props3 === void 0 ? void 0 : (_this$props3$persisto = _this$props3.persistor) === null || _this$props3$persisto === void 0 ? void 0 : _this$props3$persisto.setTokens(tokens); // Propagate events up

      (_this$props4 = _this.props) === null || _this$props4 === void 0 ? void 0 : (_this$props4$onTokens = _this$props4.onTokens) === null || _this$props4$onTokens === void 0 ? void 0 : _this$props4$onTokens.call(_this$props4, tokens);
    });

    var initConfig = props.initConfig,
        keycloakConfig = props.keycloakConfig,
        persistor = props.persistor;
    var cachedTokens = persistor.getTokens();
    _this.state = {
      keycloakStub: getKeycloakStub(persistor),
      keycloak: getKeycloakInstance(keycloakConfig),
      initConfig: _objectSpread(_objectSpread({}, cachedTokens), initConfig)
    };
    return _this;
  }

  var _proto = SSRKeycloakProvider.prototype;

  _proto.render = function render() {
    var _this$props5 = this.props,
        children = _this$props5.children,
        persistor = _this$props5.persistor,
        providerProps = _objectWithoutPropertiesLoose(_this$props5, ["children", "persistor"]); // eslint-disable-line no-unused-vars


    var _this$state = this.state,
        initConfig = _this$state.initConfig,
        keycloak = _this$state.keycloak,
        keycloakStub = _this$state.keycloakStub;
    return /*#__PURE__*/createElement(KeycloakStubProvider, {
      keycloakStub: keycloakStub
    }, /*#__PURE__*/createElement(KeycloakProvider, _extends({}, providerProps, {
      keycloak: keycloak,
      initConfig: initConfig,
      onEvent: this.onEvent,
      onTokens: this.onTokens
    }), children));
  };

  return SSRKeycloakProvider;
}(PureComponent);

process.env.NODE_ENV !== "production" ? SSRKeycloakProvider.propTypes = {
  children: PropTypes.element.isRequired,
  initConfig: PropTypes.shape({}),
  keycloakConfig: PropTypes.shape({}).isRequired,
  persistor: PropTypes.shape({
    setTokens: PropTypes.func.isRequired,
    getTokens: PropTypes.func.isRequired,
    resetTokens: PropTypes.func.isRequired
  }).isRequired
} : void 0;

var getDisplayName = function getDisplayName(name) {
  return "WithKeycloak(" + name + ")";
};

function withKeycloak(WrappedComponent) {
  var displayName = getDisplayName(WrappedComponent.displayName || WrappedComponent.name);

  var WithKeycloakComponent = /*#__PURE__*/function (_React$PureComponent) {
    _inheritsLoose(WithKeycloakComponent, _React$PureComponent);

    function WithKeycloakComponent() {
      var _this;

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      _this = _React$PureComponent.call.apply(_React$PureComponent, [this].concat(args)) || this;

      _defineProperty(_assertThisInitialized(_this), "renderWrappedComponent", function (keycloakStub) {
        return function (_ref) {
          var initialized = _ref.initialized,
              keycloak = _ref.keycloak;
          return /*#__PURE__*/createElement(WrappedComponent, _extends({}, _this.props, {
            isServer: isServer(),
            keycloak: !initialized || isServer() ? keycloakStub : keycloak,
            keycloakInitialized: initialized || isServer()
          }));
        };
      });

      return _this;
    }

    var _proto = WithKeycloakComponent.prototype;

    _proto.render = function render() {
      var _this2 = this;

      return /*#__PURE__*/createElement(KeycloakStubContext.Consumer, null, function (keycloakStub) {
        return /*#__PURE__*/createElement(KeycloakContext.Consumer, null, _this2.renderWrappedComponent(keycloakStub));
      });
    };

    return WithKeycloakComponent;
  }(PureComponent);

  WithKeycloakComponent.WrappedComponent = WrappedComponent;
  WithKeycloakComponent.displayName = displayName;
  return hoistStatics(WithKeycloakComponent, WrappedComponent);
}

function useKeycloak() {
  var keycloakStub = useContext(KeycloakStubContext);

  var _useContext = useContext(KeycloakContext),
      initialized = _useContext.initialized,
      keycloak = _useContext.keycloak;

  var isServerCheck = isServer();
  var kcInstance = !initialized || isServerCheck ? keycloakStub : keycloak;
  var kcInitialized = initialized || isServerCheck;
  return Object.assign([kcInstance, kcInitialized, isServerCheck], {
    initialized: kcInitialized,
    isServer: isServerCheck,
    keycloak: kcInstance
  });
}

function setCookie(name, val) {
  return Cookie.set(name, val);
}

function getCookie(name) {
  return Cookie.get(name);
}

function removeCookie(name) {
  Cookie.remove(name);
}

var Cookies = {
  setTokens: function setTokens(_ref) {
    var idToken = _ref.idToken,
        token = _ref.token;
    !!token && setCookie('kcToken', btoa(token));
    !!idToken && setCookie('kcIdToken', btoa(idToken));
  },
  getTokens: function getTokens() {
    var tknStr = getCookie('kcToken');
    var idTknStr = getCookie('kcIdToken');
    return {
      idToken: idTknStr ? atob(idTknStr) : undefined,
      token: tknStr ? atob(tknStr) : undefined
    };
  },
  resetTokens: function resetTokens() {
    removeCookie('kcToken');
    removeCookie('kcIdToken');
  }
};

var client = /*#__PURE__*/Object.freeze({
__proto__: null,
Cookies: Cookies
});

function setCookie$1(name, val) {
  return Cookie.set(name, val);
}
function getCookie$1(name, cookies) {
  if (cookies === void 0) {
    cookies = {};
  }

  return isServer() ? cookies[name] : Cookie.get(name);
}
function removeCookie$1(name) {
  Cookie.remove(name);
}
function encode(value) {
  return isServer() ? base64.encode(value) : btoa(value);
}
function decode(value) {
  return isServer() ? base64.decode(value) : atob(value);
}

var ExpressCookies = function ExpressCookies(req) {
  return {
    setTokens: function setTokens() {},
    getTokens: function getTokens() {
      var _ref = req.cookies || {},
          kcIdToken = _ref.kcIdToken,
          kcToken = _ref.kcToken;

      return {
        idToken: kcIdToken ? base64.decode(kcIdToken) : undefined,
        token: kcToken ? base64.decode(kcToken) : undefined
      };
    },
    resetTokens: function resetTokens() {}
  };
};
var SSRCookies = function SSRCookies(cookies) {
  return {
    setTokens: function setTokens(_ref2) {
      var idToken = _ref2.idToken,
          token = _ref2.token;
      !!token && setCookie$1('kcToken', encode(token));
      !!idToken && setCookie$1('kcIdToken', encode(idToken));
    },
    getTokens: function getTokens() {
      var tknStr = getCookie$1('kcToken', cookies);
      var idTknStr = getCookie$1('kcIdToken', cookies);
      return {
        idToken: idTknStr ? decode(idTknStr) : '',
        refreshToken: '',
        token: tknStr ? decode(tknStr) : ''
      };
    },
    resetTokens: function resetTokens() {
      removeCookie$1('kcToken');
      removeCookie$1('kcIdToken');
    }
  };
};

var server = /*#__PURE__*/Object.freeze({
__proto__: null,
ExpressCookies: ExpressCookies,
SSRCookies: SSRCookies
});

export { client as ClientPersistors, SSRKeycloakProvider, server as ServerPersistors, getKeycloakInstance, useKeycloak, withKeycloak };
